import React, { useState, useEffect } from 'react';
import {
    Modal, Container, Row, Col, Card,
    Form, Button, Spinner
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getSignedImageUrl } from './useTransactionDetails';
import { MATERIAL_OPTIONS } from './LineItemModal';

function buildEmptyLineItem(type) {
    return {
        title: '',
        description: '',
        material: '',
        quantity: 1,
        cost: 0,
        discountApplied: false,
        discountAmount: 0,
        taxExempt: false,
        weighIn: '',
        weighOut: '',
        imageURL: '',
        thumbnailURL: '',
        imageKey: '',
        lineItemType: type,
    };
}

/**
 * CreateLineItemModal
 *
 * Props:
 *   show            {boolean}
 *   type            {string}       – ticket type ('repair', 'sale', etc.)
 *   transactionId   {string}
 *   onClose         {() => void}
 *   onCreate        {(item) => Promise<void>}
 *   onUploadImage   {(file) => Promise<{ key: string, imageUrl: string }>}
 *   onDeleteImage   {(key: string) => Promise<void>}
 *   materialOptions {string[]}
 */
export function CreateLineItemModal({
    show,
    type,
    transactionId,
    onClose,
    onCreate,
    onUploadImage,
    onDeleteImage,
    materialOptions = MATERIAL_OPTIONS,
}) {
    const { t } = useTranslation();

    const [item, setItem] = useState(null);
    const [previewImageUrl, setPreviewImageUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    // Track whether an image was uploaded but the modal was not yet saved,
    // so we can clean it up on cancel.
    const [pendingImageKey, setPendingImageKey] = useState(null);

    // Reset state whenever the modal opens
    useEffect(() => {
        if (show) {
            setItem(buildEmptyLineItem(type));
            setPreviewImageUrl(null);
            setPendingImageKey(null);
        }
    }, [show, type]);

    // Refresh the signed preview URL when imageKey changes
    useEffect(() => {
        if (!item?.imageKey) return;
        let isMounted = true;
        getSignedImageUrl(item.imageKey, type, transactionId).then((url) => {
            if (isMounted) setPreviewImageUrl(url);
        });
        return () => { isMounted = false; };
    }, [item?.imageKey, type, transactionId]);

    // ── Field helpers ─────────────────────────────────────────────────────────
    const setField = (field, value) =>
        setItem((prev) => ({ ...prev, [field]: value }));

    const setNumber = (field, value) =>
        setItem((prev) => {
            const next = value === '' ? '' : Number(value);
            const updated = { ...prev, [field]: next };
            if (field === 'discountAmount') {
                updated.discountApplied = value !== '' && Number(value) > 0;
            }
            return updated;
        });

    const setCheck = (field, checked) =>
        setItem((prev) => ({ ...prev, [field]: checked }));

    // ── Image upload ──────────────────────────────────────────────────────────
    const handleImageUpload = async (file) => {
        if (!file) return;
        try {
            setIsUploading(true);
            const { key, imageUrl } = await onUploadImage(file);
            setItem((prev) => ({
                ...prev,
                imageURL: imageUrl,
                thumbnailURL: imageUrl,
                imageKey: key,
            }));
            setPendingImageKey(key);
        } catch (error) {
            console.error('Error uploading image:', error);
        } finally {
            setIsUploading(false);
        }
    };

    // ── Create ────────────────────────────────────────────────────────────────
    const handleCreate = async () => {
        if (!item) return;
        try {
            setIsCreating(true);
            await onCreate(item);
            setPendingImageKey(null);
            onClose();
        } catch (error) {
            console.error('Error creating line item:', error);
        } finally {
            setIsCreating(false);
        }
    };

    // ── Cancel – clean up any uploaded-but-unsaved image ─────────────────────
    const handleClose = async () => {
        try {
            if (pendingImageKey) {
                await onDeleteImage(pendingImageKey);
            }
        } catch (error) {
            console.error('Error cleaning up unsaved image:', error);
        } finally {
            setPendingImageKey(null);
            onClose();
        }
    };

    const busy = isCreating || isUploading;
    const imageDisplay = previewImageUrl || item?.thumbnailURL;

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered fullscreen>
            <Modal.Header closeButton>
                <Modal.Title>{t('Create Line Item')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {!item ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Container fluid>
                        <Row className="mb-4">
                            {/* ── Image panel ── */}
                            <Col md={4}>
                                <Card className="h-100">
                                    <Card.Header>{t('Images')}</Card.Header>
                                    <Card.Body className="text-center">
                                        {item.imageKey ? (
                                            <img
                                                src={imageDisplay || ''}
                                                alt={item.title || 'Uploaded Line Item Image'}
                                                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <div>{t('No image uploaded')}</div>
                                        )}

                                        <Form.Group className="mt-3">
                                            <Form.Label className="btn btn-outline-primary mb-0">
                                                {isUploading ? t('Uploading...') : t('Upload Image')}
                                                <Form.Control
                                                    type="file"
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    disabled={busy}
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) handleImageUpload(file);
                                                        e.target.value = '';
                                                    }}
                                                />
                                            </Form.Label>
                                        </Form.Group>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* ── Form panels ── */}
                            <Col md={8}>
                                {/* General */}
                                <Card className="mb-4">
                                    <Card.Header>{t('General Information')}</Card.Header>
                                    <Card.Body>
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('Title')}</Form.Label>
                                            <Form.Control
                                                value={item.title || ''}
                                                onChange={(e) => setField('title', e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-0">
                                            <Form.Label>{t('Description')}</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                value={item.description || ''}
                                                onChange={(e) => setField('description', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Card.Body>
                                </Card>

                                {/* Pricing */}
                                <Card className="mb-4">
                                    <Card.Header>{t('Pricing')}</Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('Quantity')}</Form.Label>
                                                    <Form.Control
                                                        type="number" min="1" step="1"
                                                        value={item.quantity ?? 1}
                                                        onChange={(e) => setNumber('quantity', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('Cost')}</Form.Label>
                                                    <Form.Control
                                                        type="number" step="0.01" min="0"
                                                        value={item.cost ?? ''}
                                                        onChange={(e) => setNumber('cost', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('Discount Amount')}</Form.Label>
                                                    <Form.Control
                                                        type="number" step="0.01" min="0"
                                                        value={item.discountAmount ?? ''}
                                                        onChange={(e) => setNumber('discountAmount', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={4}>
                                                <Form.Check
                                                    type="switch"
                                                    id="create-discount-applied-switch"
                                                    label={t('Discount Applied')}
                                                    checked={Boolean(item.discountApplied)}
                                                    onChange={(e) => setCheck('discountApplied', e.target.checked)}
                                                />
                                            </Col>
                                            <Col md={4}>
                                                <Form.Check
                                                    type="switch"
                                                    id="create-tax-exempt-switch"
                                                    label={t('Tax Exempt')}
                                                    checked={Boolean(item.taxExempt)}
                                                    onChange={(e) => setCheck('taxExempt', e.target.checked)}
                                                />
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Material & Weight */}
                                <Card>
                                    <Card.Header>{t('Material & Weight')}</Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('Material')}</Form.Label>
                                                    <Form.Select
                                                        value={item.material || ''}
                                                        onChange={(e) => setField('material', e.target.value)}
                                                    >
                                                        <option value="">{t('Select Material')}</option>
                                                        {materialOptions.map((m) => (
                                                            <option key={m} value={m}>{m}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('Weigh In')}</Form.Label>
                                                    <Form.Control
                                                        type="number" step="0.01"
                                                        value={item.weighIn ?? ''}
                                                        onChange={(e) => setNumber('weighIn', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>{t('Weigh Out')}</Form.Label>
                                                    <Form.Control
                                                        type="number" step="0.01"
                                                        value={item.weighOut ?? ''}
                                                        onChange={(e) => setNumber('weighOut', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                )}
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between">
                <Button
                    variant="primary"
                    onClick={handleCreate}
                    disabled={busy || !item}
                >
                    {isCreating ? t('Creating...') : t('Create Line')}
                </Button>
                <Button variant="secondary" onClick={handleClose} disabled={busy}>
                    {t('Cancel')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
